/**
 * Build JSON attachment parts for POST /mcp/tasks/attachments:
 * local paths → base64 `data`; http(s) strings → `url` (server fetches).
 */

import { readFile } from 'fs/promises';
import { basename, extname } from 'path';
import { statSync } from 'fs';

const MAX_BYTES_PER_FILE = 15 * 1024 * 1024;

const EXT_TO_MIME: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.pdf': 'application/pdf',
  '.md': 'text/markdown',
  '.markdown': 'text/markdown',
  '.txt': 'text/plain',
  '.json': 'application/json',
  '.csv': 'text/csv',
  '.html': 'text/html',
  '.htm': 'text/html',
};

export interface FileAttachPart {
  filename: string;
  content_type: string;
  /** Base64 raw bytes; omit when `url` is set */
  data?: string;
  /** Remote file location; omit when `data` is set */
  url?: string;
}

/** True if the value should be sent as a URL attachment (not read from disk). */
export function isHttpUrlInput(s: string): boolean {
  return /^https?:\/\//i.test(s.trim());
}

/**
 * Infer MIME type from file path or URL path; default application/octet-stream.
 */
export function guessContentType(fileOrUrlPath: string): string {
  const ext = extname(fileOrUrlPath).toLowerCase();
  return EXT_TO_MIME[ext] ?? 'application/octet-stream';
}

function filenameFromHttpUrl(urlString: string): string {
  const u = new URL(urlString);
  const base = basename(u.pathname);
  if (base && base !== '/' && base.length > 0) return decodeURIComponent(base);
  return 'attachment';
}

function guessContentTypeFromHttpUrl(urlString: string): string {
  try {
    return guessContentType(new URL(urlString).pathname);
  } catch {
    return 'application/octet-stream';
  }
}

/** Prefer magic bytes over extension (Cursor sometimes saves JPEG data as `.png`). */
function sniffContentType(buf: Buffer): string | undefined {
  if (buf.length >= 8 && buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47) {
    return 'image/png';
  }
  if (buf.length >= 3 && buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    buf.length >= 12 &&
    buf.toString('ascii', 0, 4) === 'RIFF' &&
    buf.toString('ascii', 8, 12) === 'WEBP'
  ) {
    return 'image/webp';
  }
  if (buf.length >= 6 && buf.toString('ascii', 0, 6) === 'GIF87a') return 'image/gif';
  if (buf.length >= 6 && buf.toString('ascii', 0, 6) === 'GIF89a') return 'image/gif';
  if (buf.length >= 4 && buf.toString('ascii', 0, 4) === '%PDF') return 'application/pdf';
  return undefined;
}

/**
 * Align filename extension with sniffed MIME so validators that cross-check are satisfied.
 */
function filenameForAttachment(originalPath: string, contentType: string): string {
  const base = basename(originalPath);
  const ext = extname(base).toLowerCase();
  const stem = base.slice(0, Math.max(0, base.length - ext.length));
  if (contentType === 'image/jpeg' && ext === '.png') return `${stem}.jpg`;
  if (contentType === 'image/png' && (ext === '.jpg' || ext === '.jpeg')) return `${stem}.png`;
  return base;
}

/**
 * Each entry is either a local filesystem path or an http(s) URL string.
 * URLs are passed through as `{ url }` for the API to fetch.
 */
export async function buildAttachmentFileParts(inputs: string[]): Promise<FileAttachPart[]> {
  const unique = [...new Set(inputs.map((s) => s.trim()).filter(Boolean))];
  const parts: FileAttachPart[] = [];

  for (const p of unique) {
    if (isHttpUrlInput(p)) {
      parts.push({
        filename: filenameFromHttpUrl(p),
        content_type: guessContentTypeFromHttpUrl(p),
        url: p,
      });
      continue;
    }

    let size: number;
    try {
      size = statSync(p).size;
    } catch {
      throw new Error(`Cannot read file: ${p}`);
    }
    if (size > MAX_BYTES_PER_FILE) {
      throw new Error(`File too large (${size} bytes, max ${MAX_BYTES_PER_FILE}): ${p}`);
    }
    const buf = await readFile(p);
    const contentType = sniffContentType(buf) ?? guessContentType(p);
    parts.push({
      filename: filenameForAttachment(p, contentType),
      content_type: contentType,
      data: buf.toString('base64'),
    });
  }

  return parts;
}

/** @deprecated Use buildAttachmentFileParts — it supports paths and http(s) URLs */
export async function readFilesAsAttachParts(paths: string[]): Promise<FileAttachPart[]> {
  return buildAttachmentFileParts(paths);
}
